import classNames from "classnames";
import { FC, useEffect, useMemo, useRef, useState } from "react";
import { DocumentGridStateType, ISaveFocus } from "../../../@types/documents";
import { DocumentStatusType, DocumentType, IdTableVariant, LoadingStatus } from "../../../@types/enumsGlobal";
import DefaultDocumentsCommandsPanelV3 from "../../../components/commandsPanels/DefaultDocumentsCommandsPanelV3";
import { DefaultGrid } from "../../../components/grids/default/defaultGrid";
import { PluginWrapper, ToolbarWrapper } from "../../../components/plugins";
import { TabsPanel } from "../../../components/tabs";
import { InventoryVedDataProvider } from "../../../Services/DataProviders/InventoryVedDataProvider";
import useGridFilter, { DefaultDateGridFilter } from "../../../system/hooks/useGridFilter";
import { useDetailsTabsPanel } from "../../../system/hooks/useTabsPanel";
import { useAppContext } from "../../../system/providers/appContextProvider";
import { usePluginContext } from "../../../system/providers/plugin";
import { useTabsContext } from "../../../system/providers/tabsProvider";
import tabsStyles from "../../../pages/styles/homePage.module.scss";
import { KizDataProvider } from "../../../Services/DataProviders/KizDataProvider";
import styles from "./styles/index.module.scss";
import { Spinner } from "../../../components/spiner/Spinner";
import { UserActionLogModal } from "../../Service/UserActionLog/view/UserActionLogModal";
import { IInventoryVedViewDTO } from "../../../libs/coreapi-dto/documents/invoice/inventory/inventoryVed";
import { useTranslation } from "react-i18next";
import { TourStep } from "../../../components/tour/Tour";
import { useTourEducation } from "../../../components/tour/TourContext";
import { getInventoryVedDocumentCommandsPanelTourSteps, getInventoryVedGridTourSteps } from "../../../components/tour/TourSteps/InventoryVedGridTourSteps";

export const InventoryVedGrid: FC<IGridProps> = (props) => {
    const appContext = useAppContext();
    const pluginCtx = usePluginContext();
    const tabCtx = useTabsContext();
    const { registerTour } = useTourEducation();

    const inventoryVedDP = new InventoryVedDataProvider(appContext.coreApiService);
    const kizDP = new KizDataProvider(appContext.coreApiService);

    const [viewState, setViewState] = useState<DocumentGridStateType>("view");
    const [gridFilter, dispatchGridFilter] = useGridFilter(DefaultDateGridFilter());
    const [data, setData] = useState<IInventoryVedViewDTO[]>([]);
    const [loadingStatus, setLoadingStatus] = useState<LoadingStatus>(LoadingStatus.Completed);
    const [totalCount, setTotalCount] = useState<number>(0);
    const [showDetailsTabs, setShowDetailsTabs] = useState<boolean>(false);

    const [detailsTabsPanel, dispatchDetailsTabsPanel] = useDetailsTabsPanel(props.plugin.mnemocode);

    const [documentStatusType, setDocumentStatusType] = useState<DocumentStatusType | undefined>();
    const [selectedDocumentState, setSelectedDocumentState] = useState<DocumentStatusType | undefined>();
    const [multipleSelect, setMultipleSelect] = useState<boolean>(false);
    const [selectedItem, setSelectedItem] = useState<IGridRow>();
    const [selectedItemView, setSelectedItemViews] = useState<IInventoryVedViewDTO>();

    const [selectedItems, setSelectedItems] = useState<IGridRow[]>([]);

    const [impossibilityProcess, setImpossibilityProcess] = useState<boolean>(false);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(true);
    
    const [showModalLog, setShowModalLog] = useState<boolean>(false);

    // Refs и шаги для tour этой вкладки.
    const [documentCommandsPanelTourSteps, setDocumentCommandsPanelTourSteps] = useState<TourStep[]>([]);
    const inventoryVedGridTourRef = useRef<HTMLDivElement>(null);
    const inventoryVedDetailsTabsTourRef = useRef<HTMLDivElement>(null);
    const currentIdTab = useRef(tabCtx.currentTab?.id);

    const [saveFocus, setSaveFocus] = useState<ISaveFocus>({
        viewState: 'view',
        selectedItem: selectedItem,
        data: data
    });

    // Локализация
    const { t } = useTranslation();

    // Собираем сценарий tour для инвентаризационных ведомостей.
    const inventoryVedTourSteps = useMemo<TourStep[]>(() => {
        return getInventoryVedGridTourSteps({
            grid: inventoryVedGridTourRef,
            detailsTabs: inventoryVedDetailsTabsTourRef,
        }, documentCommandsPanelTourSteps)
    }, [documentCommandsPanelTourSteps])

    // Регистрируем tour, чтобы кнопка обучения в QuickAccessBar стала активной.
    useEffect(() => {
        return registerTour({
            plugin: "inventory_ved_plugin",
            tabId: currentIdTab.current,
            steps: inventoryVedTourSteps,
        })
    }, [registerTour, inventoryVedTourSteps])

    useEffect(() => {
        setIsSubmitting(true);
        inventoryVedDP.getView(gridFilter, async (data, totalCount) => {
            setData(data);
            setTotalCount(totalCount);
            setIsSubmitting(false);
            setLoadingStatus(totalCount > 0 ? LoadingStatus.Completed : LoadingStatus.NoData);
            if (viewState === "refresh") {
                setViewState("view");
            }
        });
    }, [gridFilter, viewState]);

    useEffect(() => {
        if (tabCtx.currentTab?.title === t("documents.invoiceOut.tabName")) {
            setShowDetailsTabs(true);
        } else {
            setShowDetailsTabs(false);
        }
        pluginCtx.masterGrid.onUnselectEvent();
        setShowDetailsTabs(true);
    }, [tabCtx.currentTab]);

    useEffect(() => {
        if (!multipleSelect) {
            setSelectedItems([]);
        } else {
            setSelectedItem(undefined);
        }

        if (selectedItem) {
            const documentState = selectedItem.cells.find((c) => c.propertyName === "documentState")?.value as DocumentStatusType;
            setSelectedDocumentState(documentState ?? undefined);
            setSelectedItemViews(data.find((x) => x.idGlobal === selectedItem.idGlobal));
            inventoryVedDP.getById(selectedItem.idGlobal, (entity) => {
                kizDP.getKizViewAsync(selectedItem.idGlobal, { numberPerPage: 100, pageNumber: 1, columnFilters: [] }, (kiz, totalCount) => {
                    let kizs = entity.items
                        ?.map((x) => {
                            if (x.isKiz) {
                                return x.quantity;
                            } else {
                                return null;
                            }
                        })
                        .filter((x) => x !== null);
                    if (kizs?.length > 0) {
                        if (
                            kizs.reduce((a, b) => {
                                return (a as number) + (b as number);
                            }) === totalCount
                        ) {
                            setImpossibilityProcess(false);
                        } else {
                            setImpossibilityProcess(true);
                        }
                    } else {
                        setImpossibilityProcess(false);
                    }
                });
            });
        } else {
            setSelectedItemViews(undefined);
        }
    }, [multipleSelect, selectedItem]);

    useEffect(() => {
        setSelectedItem(undefined);
        pluginCtx.masterGrid.onUnselectEvent();
    }, [documentStatusType]);

    const deleteRow = () => {
        if (selectedItem?.[0]?.isDeleted || selectedDocumentState === "save" || selectedDocumentState === "proc") {
            // anything else ... ups, I don't know why it's empty
        } else {
            inventoryVedDP.markDelete(selectedItem?.idGlobal as string, () => {
                setViewState("refresh");
            });
        }
    };

    return (
        <>
            <PluginWrapper>
                <ToolbarWrapper>
                    <DefaultDocumentsCommandsPanelV3
                        selectedItems={[selectedItems,setSelectedItems]}
                        selectedItem={[selectedItem, setSelectedItem]}
                        saveFocus={[saveFocus, setSaveFocus]}
                        gridFilter={[gridFilter, dispatchGridFilter]}
                        plugin="inventory_ved_create_plugin"
                        documentStatusType={[documentStatusType, setDocumentStatusType]}
                        selectedDocumentStatusType={[selectedDocumentState, setSelectedDocumentState]}
                        multipleSelect={[multipleSelect, setMultipleSelect]}
                        dataProvider={inventoryVedDP}
                        pluginSettings={props.plugin}
                        idTable={IdTableVariant.InventoryVed}
                        setViewState={(vs) => setViewState(vs)}
                        advancedFeatures
                        impossibilityAction={{ impossibilityProcess: impossibilityProcess }}
                        items={[
                            {
                                label: t("general.showUserLog"),
                                onClick: () => selectedItem && setShowModalLog(true),
                                disabled: selectedItem ? false : true,
                            },
                        ]}
                        mnemocode={selectedItemView?.mnemocode}
                        unProcessIsHide
                        /* Шаги tour внутри общей панели команд документов. */
                        tourSteps={getInventoryVedDocumentCommandsPanelTourSteps}
                        onTourStepsChange={setDocumentCommandsPanelTourSteps}
                    />
                </ToolbarWrapper>

                <div className={styles.gridWrapper}>
                    {/* Target для tour: основная таблица документов. */}
                    <div className={styles.masterGrid} ref={inventoryVedGridTourRef}>
                        {isSubmitting ? (
                            <Spinner />
                        ) : (
                            <DefaultGrid
                                autoSelect
                                showQuantity
                                openWithEnterForEdit={{
                                pluginTabContext:"inventory_ved_create_plugin",
                                mnemocode:selectedItemView?.mnemocode,
                                selectedDocumentState}}
                                gridId={props.gridId}
                                data={data}
                                documentStatus
                                separator
                                loadingStatus={loadingStatus}
                                setLoadingStatus={setLoadingStatus}
                                actionGridRow={{ delete: () => deleteRow() }}
                                filter={gridFilter}
                                hiddenPagination={undefined}
                                dataProvider={inventoryVedDP}
                                totalCount={totalCount}
                                plugin={props.plugin}
                                multipleSelect={multipleSelect}
                                selectedItem={selectedItem}
                                selectedItems={selectedItems}
                                saveFocus={[saveFocus, setSaveFocus]}
                                onSelect={(row) => {
                                    setSelectedItem(row);
                                    row ? pluginCtx.masterGrid.onSelectEvent(row, DocumentType.inventoryVed) : pluginCtx.masterGrid.onUnselectEvent();
                                }}
                                onMultipleSelect={(rows) => {
                                    setSelectedItems(rows);
                                }}
                                onSort={(i) => {
                                    dispatchGridFilter({ type: "sort", payload: i.propertyName });
                                }}
                                onFilterDelete={(i) => {
                                    i.propertyName === "documentState" && setDocumentStatusType(undefined);
                                    dispatchGridFilter({
                                        type: "deleteColumnFilter",
                                        payload: i.propertyName,
                                    });
                                }}
                                onPageNumberChange={(n) => {
                                    dispatchGridFilter({
                                        type: "changePageNumber",
                                        payload: { pageNumber: n },
                                    });
                                }}
                                onNumberPerPageChange={(n) => {
                                    dispatchGridFilter({
                                        type: "changeNumberPerPage",
                                        payload: { numberPerPage: n },
                                    });
                                }}
                            />
                        )}
                    </div>
                    <div className={styles.detailsTabView}>
                        {showDetailsTabs && detailsTabsPanel.tabs.length > 0 && selectedItem && !multipleSelect && (
                            <>
                                <div className={tabsStyles.tabsPanelWrapper}>
                                    <div
                                        id="detailsTabsPanelWrapper"
                                        ref={inventoryVedDetailsTabsTourRef}
                                        style={{
                                            position: "relative",
                                            overflow: "hidden",
                                            width: "100%",
                                        }}
                                    >
                                        <TabsPanel
                                            id="detailsTabsPanel"
                                            activeId={detailsTabsPanel.currentTab?.id}
                                            tabs={detailsTabsPanel.tabs}
                                            onActive={(id) =>
                                                dispatchDetailsTabsPanel({
                                                    type: "activate",
                                                    payload: id,
                                                })
                                            }
                                        ></TabsPanel>
                                    </div>
                                </div>
                                <div className={tabsStyles.contentWrapper}>
                                    {detailsTabsPanel.tabs.map((item) => {
                                        return (
                                            <div key={item.id}
                                                className={classNames(item.id === detailsTabsPanel.currentTab?.id ? tabsStyles.contentVisible : tabsStyles.contentHidden)}>
                                                {item.view.content}
                                            </div>
                                        );
                                    })}
                                </div>
                            </>
                        )}
                    </div>
                    {showModalLog && <UserActionLogModal idDocumentGlobal={selectedItem?.idGlobal} onClick={() => setShowModalLog(false)} />}
                </div>
            </PluginWrapper>
        </>
    );
};

export default InventoryVedGrid;
